import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Switch,
  Typography,
  FormControlLabel,
  useTheme,
  CircularProgress,
  Chip,
  Stack,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  IconButton,
  ListItem,
  ListItemText,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Snackbar from "@mui/material/Snackbar";
import { useNotificationState } from "@pages/Contents/Corporate/Notifications/context/NotificationContext";
import { useTranslation } from "react-i18next";
import { ListNotificationType } from "../../../../../../interfaces/Common";
import {
  getFlagKey,
  getUploadKey,
  // handleFileChange,
} from "@pages/Contents/Corporate/Notifications/utils/utils";
import { InputCst, SelectCst } from "@components/InputCst";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import CustomQuillEditor from "@pages/Contents/Corporate/Notifications/utils/CustomQuillEditor";
import {
  ResponseEvents,
  Step2Props,
  TagInterface,
  ValuesInterface,
} from "@pages/Contents/Corporate/Notifications/types";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { getZipWithoutDownload, ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { useParams } from "react-router-dom";
import CustomSunEditor from "@pages/Contents/BadgeAndCertificates/utils/SunEditor/CustomSunEditor";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { VisuallyHiddenInput } from "@components/InputCst/UploadInput";
import {
  bytesToMB,
  handleDownloadFile,
  handleMultiFileChange,
  handleMultiFileDelete,
} from "@pages/Contents/BadgeAndCertificates/utils/utils";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadIcon from "@mui/icons-material/Upload";
import { unzipZipFile } from "@hooks/useUnzip";

const Step2: React.FC<Step2Props> = ({ errors }) => {
  const theme = useTheme();
  const { t } = useTranslation(["notification-page"]);
  const { state, dispatch } = useNotificationState(); //GLOBAL STATE NOTIFICATIONS
  const [downloadState, setDownloadState] = React.useState<any>({
    email: "",
    applicative: "",
    push_mobile: "",
  });
  const { id, idNotification } = useParams();

  const [open, setOpen] = React.useState(false);

  const handleClick = () => {
    setOpen(true);
  };

  const { columns, dataDetail, actionsForForms, mutate } = useFetchDetail(
    ServicesURL.getCorporate,
    id || "",
    PagesID.corporate,
  );

  const MAX_SIZE_UPLOAD = 10 * 1024 * 1024; // 10 Mb

  useEffect(() => {
    if (state.email_desc) {
      const html = state.email_desc;
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      const tables = doc.querySelector("table");
      if (tables) {
        if (tables.width) {
          dispatch({
            type: "SET_INPUT_VALUES",
            payload: {
              key: "table_width",
              value: tables.width,
            },
          });
        }
        if (tables.getAttribute("height")) {
          dispatch({
            type: "SET_INPUT_VALUES",
            payload: {
              key: "table_height",
              value: tables.getAttribute("height"),
            },
          });
        }
        if (tables.cellSpacing) {
          dispatch({
            type: "SET_INPUT_VALUES",
            payload: {
              key: "table_cell_spacing",
              value: tables.cellSpacing,
            },
          });
        }
        if (tables.cellPadding) {
          dispatch({
            type: "SET_INPUT_VALUES",
            payload: {
              key: "table_cell_padding",
              value: tables.cellPadding,
            },
          });
        }
        if (tables.border) {
          dispatch({
            type: "SET_INPUT_VALUES",
            payload: {
              key: "table_border",
              value: tables.border,
            },
          });
        }
        if (tables.align) {
          dispatch({
            type: "SET_INPUT_VALUES",
            payload: {
              key: "table_align",
              value: tables.align,
            },
          });
        }
      }
    }

    const zipAttachments = idNotification
      ? getZipWithoutDownload(
          `${ServicesURL.downloadNotificationAttachments.replace("idCorporate", id ? id : "1").replace("notificationId", idNotification)}`,
        ).then((zipAttachments) => {
          if (zipAttachments) {
            unzipZipFile(zipAttachments).then(
              (extractedFiles: { fileName: string; file: File }[]) => {
                dispatch({
                  type: "SET_INPUT_VALUES",
                  payload: {
                    key: "attachments",
                    value: extractedFiles.map((item) => item.file),
                  },
                });
              },
            );
          }
        })
      : null;
  }, []);

  const { dataTable: dataEvents } = useFetchTable(
    `${ServicesURL.getEvents}`,
    PagesID["corporate.notifications"],
  );

  const optionsEvents = useMemo(() => {
    if (!dataEvents) return;
    return dataEvents.map((el: ResponseEvents) => {
      return {
        value: el.name,
        ...el,
      };
    });
  }, [dataEvents]);

  const handleSwitchChange = (type: ListNotificationType) => {
    //ON SWITCH CHANGE
    const flagKey = getFlagKey(type);
    const uploadKey = getUploadKey(type);
    const isSwitchOn = !state[flagKey];

    dispatch({
      type: "SET_INPUT_VALUES",
      payload: { key: flagKey, value: isSwitchOn },
    }); //SET CATEGORY FLAG VALUE

    if (!isSwitchOn) {
      //IF SWITCH OFF REMOVE FILES UPLOADED IF EXIST FOR CURRENT CATEGORY
      dispatch({
        type: "SET_INPUT_VALUES",
        payload: { key: uploadKey, value: [] },
      });
    }
  };

  const [openUpload, setOpenUpload] = useState<ActionUploadDownloadIterface>();
  const { t: modalT } = useTranslation(["modal"]);

  const handleDownload = async (type: ListNotificationType) => {
    //TODO: DOWNLOAD FILE
    setDownloadState((prevState: any) => ({
      ...prevState,
      [type]: "downloading",
    }));

    await new Promise((resolve) => setTimeout(resolve, 2000));

    setDownloadState((prevState: any) => ({
      ...prevState,
      [type]: "completed",
    }));
  };

  const getButtonText = (type: ListNotificationType) => {
    //DOWNLOAD BUTTON LABEL
    switch (downloadState[type]) {
      case "downloading":
        return t("in_progress");
      case "completed":
        return t("downloaded");
      default:
        return t("download");
    }
  };

  const getButtonIcon = (type: ListNotificationType) => {
    //BUTTON DOWNLOAD ICON
    switch (downloadState[type]) {
      case "downloading":
        return <CircularProgress size={20} />;
      case "completed":
        return <CheckCircleOutlineIcon />;
      default:
        return <DownloadIcon />;
    }
  };

  const availableTags =
    optionsEvents.find((item: ValuesInterface) => item.value === state["event"])
      ?.availableTags ?? [];

  const sortedTags = [...availableTags].sort((a, b) =>
    a.label.localeCompare(b.label),
  );

  const typeVisibilityMap: Record<string, string[]> = {
    CUMULATIVELEARNREM: ["email"],
    WELCOME: ["email"],
    WELCOMENP: ["email"],
    RESET: ["email"],
    DEFAULT: ["email", "applicative", "push_mobile"],
  };

  const visibleTypes =
    typeVisibilityMap[state.event] || typeVisibilityMap.DEFAULT;

  const flagsCC = ["flagCcSingleUog", "flagCcAllUog"];

  const isDisabled = (flagKey: string) => {
    if (
      flagsCC.includes(flagKey) &&
      flagsCC.map((item) => state[item]).some((item) => item === true)
    ) {
      return !state[flagKey];
    }
    return false;
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        padding: "16px",
        borderRadius: "3px",
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="body1" gutterBottom>
        {t("template")}
      </Typography>
      <Grid container spacing={3}>
        {visibleTypes.map((type) => {
          const typeKey = type.toLowerCase() as ListNotificationType;
          const flagKey = getFlagKey(typeKey);
          // const uploadKey = getUploadKey(typeKey);

          return (
            <Grid item xs={12} key={type}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "16px",
                  marginBottom: "16px",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    minWidth: "150px",
                    marginRight: "16px",
                    fontWeight: "bold",
                  }}
                >
                  {t(type)}
                </Typography>
                <Box
                  sx={{
                    width: "50%", //TODO: REMOVE
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={state[flagKey]}
                        onChange={() => handleSwitchChange(typeKey)}
                      />
                    }
                    label={`${t("activate_sending_with")} ${t(typeKey)}`}
                    labelPlacement="end"
                    sx={{ marginRight: "auto", paddingBottom: "8px" }}
                  />
                  {/*DOWNLOAD & UPLOAD TEMPLATES TEMPORARY HIDED */}
                  {/*<Box*/}
                  {/*  sx={{*/}
                  {/*    flex: 1,*/}
                  {/*    display: "flex",*/}
                  {/*    justifyContent: "flex-end",*/}
                  {/*    gap: 1,*/}
                  {/*  }}*/}
                  {/*>*/}
                  {/*  <Button*/}
                  {/*    sx={{*/}
                  {/*      marginRight: "8px",*/}
                  {/*      backgroundColor: theme.palette.background.paper,*/}
                  {/*      color: theme.palette.primary.main,*/}
                  {/*      border: `1px solid ${theme.palette.primary.main}`,*/}
                  {/*      flex: 1,*/}
                  {/*      whiteSpace: "nowrap",*/}
                  {/*      borderRadius: "3px",*/}
                  {/*    }}*/}
                  {/*    startIcon={getButtonIcon(typeKey)}*/}
                  {/*    onClick={() => handleDownload(typeKey)}*/}
                  {/*    disabled={!state[flagKey]}*/}
                  {/*  >*/}
                  {/*    {getButtonText(typeKey)}*/}
                  {/*  </Button>*/}
                  {/*  <Button*/}
                  {/*    component="label"*/}
                  {/*    sx={{*/}
                  {/*      backgroundColor: theme.palette.primary.main,*/}
                  {/*      color: theme.palette.text.primary,*/}
                  {/*      flex: 1,*/}
                  {/*      borderRadius: "3px",*/}
                  {/*      whiteSpace: "nowrap",*/}
                  {/*    }}*/}
                  {/*    disabled={!state[flagKey]}*/}
                  {/*  >*/}
                  {/*    {!!state[uploadKey] && state[uploadKey].length !== 0*/}
                  {/*      ? t("uploaded")*/}
                  {/*      : t("upload")}*/}
                  {/*    <VisuallyHiddenInput*/}
                  {/*      type="file"*/}
                  {/*      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>*/}
                  {/*        handleFileChange(e, typeKey, state, dispatch)*/}
                  {/*      }*/}
                  {/*    />*/}
                  {/*  </Button>*/}
                  {/*</Box>*/}
                </Box>
              </Box>

              {state[flagKey] && (
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "stretch",
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      padding: "16px",
                      marginY: "25px",
                      borderRadius: "3px",
                      border: `1px solid ${theme.palette.divider}`,
                      flex: 8,
                      minWidth: 0,
                    }}
                  >
                    {/*<Typography variant="subtitle1" gutterBottom>*/}
                    {/*  {t("additional_settings_for")} {t(typeKey)}*/}
                    {/*</Typography>*/}

                    {/* Form condizionale in base a typeKey */}
                    {typeKey === "email" && (
                      <>
                        <SelectCst
                          label={`${t("email_sender")}`}
                          key={"email_sender"}
                          id={"email_sender"}
                          value={state["email_sender"]}
                          required={true}
                          onChange={(e: any) => {
                            dispatch({
                              type: "SET_INPUT_VALUES",
                              payload: { key: "email_sender", value: e },
                            });
                          }}
                          sx={{
                            marginY: "16px",
                          }}
                          options={
                            dataDetail?.sender_emails?.map((email: any) => ({
                              label: email,
                              value: email,
                            })) || []
                          }
                          error={!!errors["email_sender"]}
                          errorMessage={`${t(`validation.${errors["email_sender"]}`)}`}
                        />
                        <SelectCst
                          label={`${t("email_display_name")}`}
                          key={"email_display_name"}
                          id={"email_display_name"}
                          value={state["email_display_name"]}
                          required={false}
                          onChange={(e: any) => {
                            dispatch({
                              type: "SET_INPUT_VALUES",
                              payload: { key: "email_display_name", value: e },
                            });
                          }}
                          sx={{
                            marginY: "16px",
                          }}
                          options={
                            dataDetail?.display_names?.map((email: any) => ({
                              label: email,
                              value: email,
                            })) || []
                          }
                          error={!!errors["email_display_name"]}
                          errorMessage={`${t(`validation.${errors["email_display_name"]}`)}`}
                        />
                        <InputCst
                          label={`${t("subject")}`}
                          key={"email_subject"}
                          id={"email_subject"}
                          value={state["email_subject"]}
                          required={true}
                          maxValue={100}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            dispatch({
                              type: "SET_INPUT_VALUES",
                              payload: {
                                key: e.target.id,
                                value: e.target.value,
                              },
                            });
                          }}
                          sx={{
                            marginY: "16px",
                          }}
                          error={!!errors["email_subject"]}
                          errorMessage={`${t(`validation.${errors["email_subject"]}`)}`}
                          endAdornment={
                            <InputAdornment position="end">
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.palette.text.primary,
                                  mr: 1,
                                }}
                              >
                                {`${state["email_subject"]?.length || 0}/100`}
                              </Typography>
                            </InputAdornment>
                          }
                        />
                        {/* <CustomQuillEditor
                          value={state.email_desc}
                          field="email_desc"
                          onChange={(e: {
                            target: { id: string; value: string };
                          }) => {
                            dispatch({
                              type: "SET_INPUT_VALUES",
                              payload: {
                                key: e.target.id,
                                value: e.target.value,
                              },
                            });
                          }}
                        /> */}
                        <CustomSunEditor
                          value={state.email_desc}
                          isForNotification={true}
                          onChange={(e: any) => {
                            dispatch({
                              type: "SET_INPUT_VALUES",
                              payload: {
                                key: "email_desc",
                                value: e,
                              },
                            });
                          }}
                        />
                        <Accordion variant="outlined" sx={{ marginY: "16px" }}>
                          <AccordionSummary
                            expandIcon={<ArrowDropDownIcon />}
                            aria-controls="panel1-content"
                            id="panel1-header"
                          >
                            <Typography component="span">
                              {t("table")}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <InputCst
                              label={`${t("table_width")}`}
                              key={"table_width"}
                              id={"table_width"}
                              value={state["table_width"]}
                              required={false}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                dispatch({
                                  type: "SET_INPUT_VALUES",
                                  payload: {
                                    key: e.target.id,
                                    value: e.target.value,
                                  },
                                });
                              }}
                              sx={{
                                marginY: "16px",
                              }}
                              error={!!errors["table_width"]}
                              errorMessage={`${t(`validation.${errors["table_width"]}`)}`}
                            />
                            <InputCst
                              label={`${t("table_height")}`}
                              key={"table_height"}
                              id={"table_height"}
                              value={state["table_height"]}
                              required={false}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                dispatch({
                                  type: "SET_INPUT_VALUES",
                                  payload: {
                                    key: e.target.id,
                                    value: e.target.value,
                                  },
                                });
                              }}
                              sx={{
                                marginY: "16px",
                              }}
                              error={!!errors["table_height"]}
                              errorMessage={`${t(`validation.${errors["table_height"]}`)}`}
                            />
                            <InputCst
                              label={`${t("table_cell_spacing")}`}
                              key={"table_cell_spacing"}
                              id={"table_cell_spacing"}
                              value={state["table_cell_spacing"]}
                              required={false}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                dispatch({
                                  type: "SET_INPUT_VALUES",
                                  payload: {
                                    key: e.target.id,
                                    value: e.target.value,
                                  },
                                });
                              }}
                              sx={{
                                marginY: "16px",
                              }}
                              error={!!errors["table_cell_spacing"]}
                              errorMessage={`${t(`validation.${errors["table_cell_spacing"]}`)}`}
                            />
                            <InputCst
                              label={`${t("table_cell_padding")}`}
                              key={"table_cell_padding"}
                              id={"table_cell_padding"}
                              value={state["table_cell_padding"]}
                              required={false}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                dispatch({
                                  type: "SET_INPUT_VALUES",
                                  payload: {
                                    key: e.target.id,
                                    value: e.target.value,
                                  },
                                });
                              }}
                              sx={{
                                marginY: "16px",
                              }}
                              error={!!errors["table_cell_padding"]}
                              errorMessage={`${t(`validation.${errors["table_cell_padding"]}`)}`}
                            />
                            <InputCst
                              label={`${t("table_border")}`}
                              key={"table_border"}
                              id={"table_border"}
                              value={state["table_border"]}
                              required={false}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                dispatch({
                                  type: "SET_INPUT_VALUES",
                                  payload: {
                                    key: e.target.id,
                                    value: e.target.value,
                                  },
                                });
                              }}
                              sx={{
                                marginY: "16px",
                              }}
                              error={!!errors["table_border"]}
                              errorMessage={`${t(`validation.${errors["table_border"]}`)}`}
                            />
                            <SelectCst
                              label={`${t("table_align")}`}
                              key={"table_align"}
                              id={"table_align"}
                              value={state["table_align"]}
                              required={false}
                              onChange={(e: any) => {
                                dispatch({
                                  type: "SET_INPUT_VALUES",
                                  payload: {
                                    key: "table_align",
                                    value: e,
                                  },
                                });
                              }}
                              options={[
                                {
                                  label: `${t("table_align_left")}`,
                                  value: "left",
                                },
                                {
                                  label: `${t("table_align_center")}`,
                                  value: "center",
                                },
                                {
                                  label: `${t("table_align_right")}`,
                                  value: "right",
                                },
                              ]}
                              sx={{
                                marginY: "16px",
                              }}
                            />
                          </AccordionDetails>
                        </Accordion>
                        {state.event !== "ENROLLIVE" && (
                            <Accordion variant="outlined" sx={{ marginY: "16px" }}>
                              <AccordionSummary
                                expandIcon={<ArrowDropDownIcon />}
                                aria-controls="panel2-content"
                                id="panel2-header"
                              >
                                <Typography component="span">
                                  {t("cc_options")}
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Stack>
                                  <InputCst
                                    label={`${t("email_cc")}`}
                                    key={"email_cc"}
                                    id={"email_cc"}
                                    value={state["email_cc"]}
                                    required={false}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => {
                                      dispatch({
                                        type: "SET_INPUT_VALUES",
                                        payload: {
                                          key: e.target.id,
                                          value: e.target.value,
                                        },
                                      });
                                    }}
                                    sx={{
                                      marginY: "16px",
                                    }}
                                  />
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={state[getFlagKey("cc_supervisor")]}
                                        onChange={() =>
                                          handleSwitchChange("cc_supervisor")
                                        }
                                      />
                                    }
                                    label={`${t("cc_supervisor")}`}
                                    labelPlacement="end"
                                    sx={{
                                      marginRight: "auto",
                                      paddingBottom: "8px",
                                      marginY: "16px",
                                    }}
                                  />
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={state[getFlagKey("cc_single_uog")]}
                                        onChange={() =>
                                          handleSwitchChange("cc_single_uog")
                                        }
                                        disabled={isDisabled(
                                          getFlagKey("cc_single_uog"),
                                        )}
                                      />
                                    }
                                    label={`${t("cc_single_uog")}`}
                                    labelPlacement="end"
                                    sx={{
                                      marginRight: "auto",
                                      paddingBottom: "8px",
                                      marginY: "16px",
                                    }}
                                  />
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={state[getFlagKey("cc_all_uog")]}
                                        onChange={() =>
                                          handleSwitchChange("cc_all_uog")
                                        }
                                        disabled={isDisabled(
                                          getFlagKey("cc_all_uog"),
                                        )}
                                      />
                                    }
                                    label={`${t("cc_all_uog")}`}
                                    labelPlacement="end"
                                    sx={{
                                      marginRight: "auto",
                                      paddingBottom: "8px",
                                      marginY: "16px",
                                    }}
                                  />
                                </Stack>
                              </AccordionDetails>
                            </Accordion>
                        )}
                        <Grid key={"attachments"} item xs={12} sm={12}>
                          <label htmlFor={"attachments"}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                marginY: "16px",
                                justifyContent: "space-between",
                                border: `1px dashed ${theme.palette.divider}`,
                                paddingLeft: theme.spacing(3.3),
                                paddingRight: theme.spacing(0.3),
                                cursor: "pointer",
                                position: "relative",
                                "&:hover": {
                                  backgroundColor: theme.palette.action.hover,
                                },
                              }}
                            >
                              <Typography variant="body1" component="span">
                                {`${t("email_attachments")}`}
                              </Typography>
                              <IconButton
                                component="span"
                                sx={{ marginLeft: 1 }}
                              >
                                <UploadIcon />
                              </IconButton>
                              <VisuallyHiddenInput
                                id={"attachments"}
                                type="file"
                                accept=".pdf, .jpg, .jpeg, .png, .csv, .xls, .xlsx, .doc, .docx"
                                multiple
                                onChange={(e: any) =>
                                  handleMultiFileChange(
                                    e,
                                    "attachments",
                                    dispatch,
                                    MAX_SIZE_UPLOAD,
                                    1024,
                                    state.attachments,
                                  )
                                }
                                error={!!state["error_file_attachments"]}
                                errorMessage={`${t(
                                  `validation.${state["error_file_attachments"]}`,
                                  { maxSize: bytesToMB(MAX_SIZE_UPLOAD) },
                                )}`}
                              />
                            </Box>
                          </label>
                          {state?.attachments?.length > 0 && (
                            <List dense>
                              {state?.attachments?.map((item: File) => (
                                <ListItem
                                  key={item.name}
                                  secondaryAction={
                                    <>
                                      <IconButton
                                        edge="end"
                                        aria-label="open"
                                        onClick={() => {
                                          handleDownloadFile(item);
                                        }}
                                      >
                                        <FileDownloadIcon />
                                      </IconButton>

                                      <IconButton
                                        edge="end"
                                        aria-label="delete"
                                        onClick={() =>
                                          handleMultiFileDelete(
                                            "attachments",
                                            dispatch,
                                            item.name,
                                            state.attachments,
                                          )
                                        }
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </>
                                  }
                                >
                                  <ListItemText primary={item.name} />
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </Grid>
                      </>
                    )}

                    {typeKey === "push_mobile" && (
                      <>
                        <InputCst
                          label={`${t("subject")}`}
                          key={"push_mobile_subject"}
                          id={"push_mobile_subject"}
                          value={state["push_mobile_subject"]}
                          required={true}
                          maxValue={100}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            dispatch({
                              type: "SET_INPUT_VALUES",
                              payload: {
                                key: e.target.id,
                                value: e.target.value,
                              },
                            });
                          }}
                          sx={{
                            marginY: "16px",
                          }}
                          error={!!errors["push_mobile_subject"]}
                          errorMessage={`${t(`validation.${errors["push_mobile_subject"]}`)}`}
                          endAdornment={
                            <InputAdornment position="end">
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.palette.text.primary,
                                  mr: 1,
                                }}
                              >
                                {`${state["push_mobile_subject"]?.length || 0}/100`}
                              </Typography>
                            </InputAdornment>
                          }
                        />
                        <InputCst
                          label={`${t("description")}`}
                          key={"push_mobile_desc"}
                          id={"push_mobile_desc"}
                          value={state["push_mobile_desc"]}
                          required={true}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            dispatch({
                              type: "SET_INPUT_VALUES",
                              payload: {
                                key: e.target.id,
                                value: e.target.value,
                              },
                            });
                          }}
                          sx={{
                            marginY: "16px",
                          }}
                          multiline
                          rows={6}
                          error={!!errors["push_mobile_desc"]}
                          errorMessage={`${t(`validation.${errors["push_mobile_desc"]}`)}`}
                        />
                      </>
                    )}

                    {typeKey === "applicative" && (
                      <>
                        <InputCst
                          label={`${t("subject")}`}
                          key={"applicative_subject"}
                          id={"applicative_subject"}
                          value={state["applicative_subject"]}
                          required={true}
                          maxValue={100}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            dispatch({
                              type: "SET_INPUT_VALUES",
                              payload: {
                                key: e.target.id,
                                value: e.target.value,
                              },
                            });
                          }}
                          sx={{
                            marginY: "16px",
                          }}
                          error={!!errors["applicative_subject"]}
                          errorMessage={`${t(`validation.${errors["applicative_subject"]}`)}`}
                          endAdornment={
                            <InputAdornment position="end">
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.palette.text.primary,
                                  mr: 1,
                                }}
                              >
                                {`${state["applicative_subject"]?.length || 0}/100`}
                              </Typography>
                            </InputAdornment>
                          }
                        />
                        <InputCst
                          label={`${t("description")}`}
                          key={"applicative_desc"}
                          id={"applicative_desc"}
                          value={state["applicative_desc"]}
                          required={true}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            dispatch({
                              type: "SET_INPUT_VALUES",
                              payload: {
                                key: e.target.id,
                                value: e.target.value,
                              },
                            });
                          }}
                          sx={{
                            marginY: "16px",
                          }}
                          multiline
                          rows={6}
                          error={!!errors["applicative_desc"]}
                          errorMessage={`${t(`validation.${errors["applicative_desc"]}`)}`}
                        />
                      </>
                    )}
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      padding: "16px",
                      marginY: "25px",
                      borderRadius: "3px",
                      border: `1px solid ${theme.palette.divider}`,
                      flex: 2,
                    }}
                  >
                    {/*<SelectCst*/}
                    {/*    key={"event"}*/}
                    {/*    id={"event"}*/}
                    {/*    label={"Tag disponibili"}*/}
                    {/*    value={state["event"]}*/}
                    {/*    required={false}*/}
                    {/*    onChange={(e) => {*/}
                    {/*      const selected = ["tag1", "tag2", "tag3"];*/}
                    {/*      const to = selected.find(*/}
                    {/*          (item) => item === e,*/}
                    {/*      );*/}
                    {/*      setSelectedEventTags(to);*/}

                    {/*    }}*/}
                    {/*    sx={{*/}
                    {/*      marginLeft: 'auto',*/}
                    {/*    }}*/}
                    {/*/>*/}

                    {/*<Stack direction="column" spacing={1}>*/}
                    {/*  {listTags.map((el) => (*/}
                    {/*    <Chip*/}
                    {/*      label={el.label}*/}
                    {/*      sx={{ cursor: "pointer" }}*/}
                    {/*      onClick={(event) => {*/}
                    {/*        navigator.clipboard.writeText(el.value);*/}
                    {/*        handleClick();*/}
                    {/*      }}*/}
                    {/*    />*/}
                    {/*  ))}*/}
                    {/*</Stack>*/}

                    <Stack direction="column" spacing={1}>
                      {sortedTags.map((el: TagInterface) => (
                        <Chip
                          label={t(`availableTags.${el.value}`)}
                          sx={{ cursor: "pointer" }}
                          onClick={(event) => {
                            navigator.clipboard.writeText(el.value);
                            handleClick();
                          }}
                        />
                      ))}
                    </Stack>
                    <Snackbar
                      open={open}
                      autoHideDuration={1000}
                      onClose={() => setOpen(false)}
                      message="Copied to clipboard"
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    />
                  </Box>
                </div>
              )}
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default Step2;
